import {
  AcExpoEventListType,
  CashRegister,
  CashRegisterFilters,
  CashRegisterHolder,
  CashRegisterSession,
  CashRegisterSessionListWithName,
  CondensedEventType,
  EventCardSummary,
  EventDetails,
  EventEntry,
  EventPrestations,
  EventSuggestionType,
  EventSummary,
  EventTicketSale,
  EventType,
  EventWithCustomField,
  FilterEventType,
  HomePageEventType,
  MyEventsFilter,
  Paginated,
  PaginatedResponse,
  PaginatedUserEventType,
  ProEventVisitorsFilter,
  ProEventVisitorsResponse,
  PublicEventDetails,
  QueryArgs,
  Sortable,
  UserEventType,
} from "@ac-expo/api-types";
import { isNullish, toDatabaseFormat } from "@ac-expo/utils";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useCallback } from "react";
import { privateAxios } from "../axiosInstances";
import { ticketingSalesQueryKeys } from "../query-keys";
import eventsQueryKeys, {
  acExpoEventsQueryKeys,
  cashRegisterHoldersQueryKeys,
  cashRegisterSessionsQueryKeys,
  likeEventsQueryKeys,
  visitedEventsQueryKeys,
} from "../query-keys/events.query-keys";
export type { ProEventVisitorsFilter } from "@ac-expo/api-types";

const getEvents = async (filters?: FilterEventType) => {
  const res = await privateAxios.get<EventWithCustomField<CondensedEventType>[]>("/events/public", {
    params: filters,
  });
  return res.data;
};

export const useGetEventsQuery = (filters?: FilterEventType) => {
  return useQuery({
    queryKey: eventsQueryKeys.list(filters),
    queryFn: () => getEvents(filters),
  });
};

const getEventsV2 = async (params: Paginated<FilterEventType>) => {
  const res = await privateAxios.get<PaginatedResponse<EventWithCustomField<HomePageEventType>>>(
    "/events/public/v2",
    {
      params,
    },
  );
  return res.data;
};

export const useGetEventsV2Query = (
  params: Paginated<FilterEventType>,
  options?: QueryArgs<PaginatedResponse<EventWithCustomField<HomePageEventType>>>,
) => {
  return useQuery({
    queryKey: eventsQueryKeys.list(params),
    queryFn: () => getEventsV2(params),
    ...options,
  });
};

const getEventsForMap = async (params: FilterEventType) => {
  const res = await privateAxios.get<
    Array<
      Pick<
        HomePageEventType,
        "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
      >
    >
  >("/events/public/map", {
    params,
  });
  return res.data;
};

export const useGetEventsForMapQuery = (
  params: FilterEventType,
  options?: QueryArgs<
    Array<
      Pick<
        HomePageEventType,
        "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
      >
    >
  >,
) => {
  return useQuery({
    queryKey: eventsQueryKeys.map(params),
    queryFn: () => getEventsForMap(params),
    ...options,
  });
};

export const useGetEventsForMapWithAccumulation = (filters: FilterEventType) => {
  const queryClient = useQueryClient();

  const boundsKey = filters.bounds
    ? `${filters.bounds.northeast.lat.toFixed(4)},${filters.bounds.northeast.lng.toFixed(4)},${filters.bounds.southwest.lat.toFixed(4)},${filters.bounds.southwest.lng.toFixed(4)}`
    : null;

  return useQuery({
    queryKey: [...eventsQueryKeys.map(filters), "accumulation", boundsKey],
    queryFn: async () => {
      const existingCacheKey = [
        ...eventsQueryKeys.map({
          ...filters,
          bounds: undefined,
          zoomLevel: undefined,
        }),
        "accumulation",
        "base",
      ];

      let existingEvents: Array<
        Pick<
          HomePageEventType,
          "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
        >
      > = [];
      const cacheData = queryClient.getQueryData(existingCacheKey);
      if (Array.isArray(cacheData)) {
        existingEvents = cacheData as Array<
          Pick<
            HomePageEventType,
            "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
          >
        >;
      }

      const newEvents = await getEventsForMap(filters);

      const eventMap = new Map<
        string,
        Pick<
          HomePageEventType,
          "id" | "name" | "slug" | "address" | "isSponsored" | "dates" | "organiserName" | "image"
        >
      >();

      existingEvents.forEach((event) => {
        eventMap.set(event.id, event);
      });

      newEvents.forEach((event) => {
        eventMap.set(event.id, event);
      });

      return Array.from(eventMap.values());
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    enabled: !!filters.bounds,
  });
};

// TODO: Change in mobile as well: Helper hook to clear map events cache when non-bounds filters change
export const useClearMapEventsCache = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (baseFilters: FilterEventType) => {
      const baseKey = {
        ...baseFilters,
        bounds: undefined,
        zoomLevel: undefined,
      };

      queryClient.removeQueries({
        queryKey: [...eventsQueryKeys.map(baseKey), "accumulative"],
      });
    },
    [queryClient],
  );
};

const getEventsToExhibitTo = async (params: Paginated<FilterEventType>) => {
  const res = await privateAxios.get<PaginatedResponse<EventWithCustomField<HomePageEventType>>>(
    "/events/exhibit-to",
    { params },
  );
  return res.data;
};

export const useGetEventsToExhibitToQuery = (params: Paginated<FilterEventType>) => {
  return useQuery({
    queryKey: eventsQueryKeys.exhibitTo(params),
    queryFn: () => getEventsToExhibitTo(params),
  });
};

const getAllEventsForACExpo = async (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>,
) => {
  const res = await privateAxios.get<PaginatedResponse<AcExpoEventListType>>("/events/all", {
    params,
  });
  return res.data;
};

export const useGetAllEventsQuery = (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>,
  options?: QueryArgs<PaginatedResponse<AcExpoEventListType>>,
) => {
  const { address, locationDistance, ...others } = params;

  const paramsToUse = !address ? others : params;

  return useQuery({
    queryKey: acExpoEventsQueryKeys.list(paramsToUse),
    queryFn: () => getAllEventsForACExpo(paramsToUse),
    ...options,
  });
};

const getUserEvents = async (filters?: MyEventsFilter) => {
  const res = await privateAxios.get<EventWithCustomField<UserEventType>[]>("/events/mine", {
    params: filters,
  });
  return res.data;
};

export const useGetUserEventsQuery = (filters?: MyEventsFilter, disabled?: boolean) => {
  return useQuery({
    queryKey: eventsQueryKeys.me(filters),
    queryFn: () => getUserEvents(filters),
    enabled: !disabled,
  });
};

const getUserEventsPaginated = async (filters?: Sortable<Paginated<MyEventsFilter>>) => {
  const res = await privateAxios.get<
    PaginatedResponse<EventWithCustomField<PaginatedUserEventType>>
  >("/events/mine/paginated", {
    params: filters,
  });
  return res.data;
};

export const useGetUserEventsPaginatedQuery = (filters?: Sortable<Paginated<MyEventsFilter>>) => {
  return useQuery({
    queryKey: eventsQueryKeys.me(filters),
    queryFn: () => getUserEventsPaginated(filters),
  });
};

const PAGE_SIZE = 20;

export const useGetUserEventsInfiniteQuery = (
  filters?: MyEventsFilter,
  enabled = true,
) => {
  return useInfiniteQuery({
    queryKey: eventsQueryKeys.meInfinite(filters),
    queryFn: ({ pageParam }) =>
      getUserEventsPaginated({
        ...filters,
        pagination: pageParam,
        sorting: [],
      }),
    initialPageParam: { pageIndex: 0, pageSize: PAGE_SIZE },
    getNextPageParam: (
      lastPage: PaginatedResponse<EventWithCustomField<PaginatedUserEventType>>,
    ) => {
      if (
        isNullish(lastPage.pageIndex) ||
        isNullish(lastPage.pageSize) ||
        lastPage.count <= (lastPage.pageIndex + 1) * lastPage.pageSize
      )
        return undefined;

      return {
        pageIndex: lastPage.pageIndex + 1,
        pageSize: lastPage.pageSize,
      };
    },
    enabled,
    placeholderData: keepPreviousData,
  });
};

const getUserEventsAutocomplete = async (searchText: string) => {
  const res = await privateAxios.get<{ label: string; value: string }[]>(
    "/events/mine/autocomplete",
    { params: { searchText } },
  );
  return res.data;
};

export const useGetUserEventsAutocompleteQuery = (searchText: string) => {
  return useQuery({
    queryKey: eventsQueryKeys.autocomplete(searchText),
    queryFn: () => getUserEventsAutocomplete(searchText),
  });
};

/**
 * Public event, filtered by the caller's session. The optional guest session token is sent
 * as Bearer for the cross-site embed, where the session cookie is never sent.
 */
export const getEvent = async (id: string, accessToken?: string | null) => {
  const res = await privateAxios.get<PublicEventDetails>(
    `/events/public/${id}`,
    accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined,
  );
  return res.data;
};

type GetEventQueryOptions = QueryArgs<PublicEventDetails> & {
  // Guest session token presented as Bearer (embed); part of the query key.
  accessToken?: string | null;
};

export const useGetEventQuery = (id: string, options?: GetEventQueryOptions) => {
  const { accessToken, ...queryOptions } = options ?? {};
  return useQuery({
    queryKey: accessToken
      ? eventsQueryKeys.getAsGuest(id, accessToken)
      : eventsQueryKeys.get(id),
    queryFn: () => getEvent(id, accessToken),
    ...queryOptions,
  });
};

export const getEventBySlug = async (slug: string) => {
  const res = await privateAxios.get<PublicEventDetails>(`/events/public/slug/${slug}`);
  return res.data;
};

export const useGetEventBySlugQuery = (
  slug: string,
  options?: QueryArgs<PublicEventDetails>,
) => {
  return useQuery({
    queryKey: eventsQueryKeys.getBySlug(slug),
    queryFn: () => getEventBySlug(slug),
    ...options,
  });
};

const getCompleteEvent = async (id: string) => {
  const res = await privateAxios.get<EventWithCustomField<EventType>>(`/events/${id}`);

  return res.data;
};

export const useGetCompleteEventQuery = (id: string) => {
  return useQuery({
    queryKey: eventsQueryKeys.getComplete(id),
    queryFn: () => getCompleteEvent(id),
    enabled: !!id,
  });
};

const getCommercialEvents = async (commercialId?: string, filter?: string) => {
  const now = new Date();
  const today = toDatabaseFormat(now);
  let dateFilter = {};

  if (filter === "upcoming") {
    dateFilter = {
      dates: {
        schedules: {
          every: {
            fromDate: {
              gt: today,
            },
          },
        },
      },
    };
  } else if (filter === "ongoing") {
    dateFilter = {
      dates: {
        schedules: {
          some: {
            AND: [
              {
                fromDate: {
                  lte: today,
                },
              },
              {
                toDate: {
                  gte: today,
                },
              },
            ],
          },
        },
      },
    };
  } else if (filter === "past") {
    dateFilter = {
      dates: {
        schedules: {
          every: {
            toDate: {
              lt: today,
            },
          },
        },
      },
    };
  } else if (filter === "upcoming_and_ongoing") {
    dateFilter = {
      dates: {
        schedules: {
          some: {
            toDate: {
              gte: today,
            },
          },
        },
      },
    };
  }

  const res = await privateAxios.get<CondensedEventType[]>(`/events/commercial`, {
    params: {
      commercialId,
      dateFilter: JSON.stringify(dateFilter),
    },
  });
  return res.data;
};

export const useGetCommercialEventsQuery = (commercialId?: string, filter?: string) => {
  return useQuery({
    queryKey: ["events", "commercial", filter, commercialId],
    queryFn: () => getCommercialEvents(commercialId, filter),
  });
};

const getEventTicketSales = async (eventId: string) => {
  const { data } = await privateAxios.get<EventTicketSale[]>(`/orders/events/${eventId}`);
  return data;
};

export const useGetEventTicketSalesQuery = (eventId: string) => {
  return useQuery({
    queryKey: ticketingSalesQueryKeys.event(eventId),
    queryFn: () => getEventTicketSales(eventId),
  });
};

const getEventPaidPrestations = async (id: string) => {
  const res = await privateAxios.get<EventPrestations>(`/events/prestations/${id}`);
  return res.data;
};

export function useGetEventPaidPrestations(eventId: string) {
  return useQuery({
    queryKey: ["prestations", "event", eventId],
    queryFn: () => getEventPaidPrestations(eventId),
  });
}

const getReportedEventsForACExpo = async (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>,
) => {
  const res = await privateAxios.get<PaginatedResponse<AcExpoEventListType>>("/events/reported", {
    params,
  });
  return res.data;
};

export const useGetReportedEventsQuery = (
  params: Sortable<Paginated<Omit<FilterEventType, "sortBy">>>,
  options: QueryArgs<PaginatedResponse<AcExpoEventListType>>,
) => {
  return useQuery({
    queryKey: acExpoEventsQueryKeys.reported(params),
    queryFn: () => getReportedEventsForACExpo(params),
    ...options,
  });
};

const getAutoCompleteSuggestions = async (searchText: string, organiserId?: string) => {
  const res = await privateAxios.get<EventSuggestionType[]>("/events/autocomplete", {
    params: {
      searchText,
      organiserId,
    },
  });
  return res.data;
};

export const useGetAutoCompleteSuggestions = (
  searchText: string,
  organiserId?: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: acExpoEventsQueryKeys.suggestions(searchText, organiserId),
    queryFn: () => getAutoCompleteSuggestions(searchText, organiserId),
    enabled,
  });
};

const getEventLikesSummaries = async () => {
  const res = await privateAxios.get<EventSummary[]>(`/events/like/summaries`);
  return res.data;
};

export const useGetEventLikesSummariesQuery = () => {
  return useQuery({
    queryKey: likeEventsQueryKeys.summaries(),
    queryFn: () => getEventLikesSummaries(),
  });
};

const getEventVisitedSummaries = async () => {
  const res = await privateAxios.get<EventSummary[]>(`/events/visited/summaries`);
  return res.data;
};

export const useGetEventVisitedSummariesQuery = () => {
  return useQuery({
    queryKey: visitedEventsQueryKeys.summaries(),
    queryFn: () => getEventVisitedSummaries(),
  });
};

const getEventLikesSummariesCard = async () => {
  const res = await privateAxios.get<EventCardSummary[]>(`/events/like/summaries/card`);
  return res.data;
};

export const useGetEventLikesSummariesCardQuery = (options?: QueryArgs<EventCardSummary[]>) => {
  return useQuery({
    queryKey: likeEventsQueryKeys.summariesCard(),
    queryFn: () => getEventLikesSummariesCard(),
    ...options,
  });
};

const getEventVisitedSummariesCard = async () => {
  const res = await privateAxios.get<EventCardSummary[]>(`/events/visited/summaries/card`);
  return res.data;
};

export const useGetEventVisitedSummariesCardQuery = (options?: QueryArgs<EventCardSummary[]>) => {
  return useQuery({
    queryKey: visitedEventsQueryKeys.summariesCard(),
    queryFn: () => getEventVisitedSummariesCard(),
    ...options,
  });
};

const getEventLikesIds = async () => {
  const res = await privateAxios.get<string[]>(`/events/like`);
  return res.data;
};

export const useGetEventLikesIdsQuery = () => {
  return useQuery({
    queryKey: likeEventsQueryKeys.ids(),
    queryFn: () => getEventLikesIds(),
  });
};

const getCashRegisterHolders = async (eventId: string) => {
  const res = await privateAxios.get<CashRegisterHolder[]>(
    `/events/${eventId}/cash-register/holders`,
  );
  return res.data;
};

export const useGetCashRegisterHolders = (eventId: string) => {
  return useQuery({
    queryKey: cashRegisterHoldersQueryKeys.byEvent(eventId),
    queryFn: () => getCashRegisterHolders(eventId),
  });
};

const getDailySessions = async (eventId: string, date: string) => {
  const res = await privateAxios.get<CashRegisterSession[]>(
    `/events/${eventId}/cash-register/sessions`,
    {
      params: {
        date,
      },
    },
  );
  return res.data;
};

export const useGetDailySessions = (eventId: string, date: string) => {
  return useQuery({
    queryKey: cashRegisterSessionsQueryKeys.byEventAndDate(eventId, date),
    queryFn: () => getDailySessions(eventId, date),
  });
};

const getDailySessionsByCashRegister = async (
  eventId: string,
  date: string,
  cashRegisterId: string,
) => {
  const res = await privateAxios.get<CashRegisterSessionListWithName>(
    `/events/${eventId}/cash-register/sessions/by-cash-register/${cashRegisterId}`,
    {
      params: {
        date,
      },
    },
  );
  return res.data;
};

export const useGetDailySessionsByCashRegister = (
  eventId: string,
  date: string,
  cashRegisterId: string,
  enabled = true,
) => {
  return useQuery({
    queryKey: cashRegisterSessionsQueryKeys.byEventAndDateAndCashRegister(
      eventId,
      date,
      cashRegisterId,
    ),
    queryFn: () => getDailySessionsByCashRegister(eventId, date, cashRegisterId),
    enabled,
  });
};

const getSession = async (eventId: string, sessionId: string) => {
  const res = await privateAxios.get<CashRegisterSessionListWithName>(
    `/events/${eventId}/cash-register/sessions/${sessionId}`,
  );
  return res.data;
};

export const useGetSession = (eventId: string, sessionId: string, enabled = true) => {
  return useQuery({
    queryKey: cashRegisterSessionsQueryKeys.byEventAndId(eventId, sessionId),
    queryFn: () => getSession(eventId, sessionId),
    enabled,
  });
};

export const useGetSessionWithIdOrCashRegisterId = (
  byCashRegister: boolean,
  eventId: string,
  date: string,
  id: string,
) => {
  if (byCashRegister) return useGetDailySessionsByCashRegister(eventId, date, id);
  return useGetSession(eventId, id);
};

const getEntries = async (eventId: string): Promise<EventEntry[]> => {
  const res = await privateAxios.get<EventEntry[]>(`/events/${eventId}/entries`);
  return res.data;
};

export const useGetEntries = (eventId: string) => {
  return useQuery({
    queryKey: eventsQueryKeys.entries(eventId),
    queryFn: () => getEntries(eventId),
  });
};

const getCashRegisters = async (eventId: string): Promise<CashRegister[]> => {
  const res = await privateAxios.get<CashRegister[]>(`/events/${eventId}/cash-register`);
  return res.data;
};

export const useGetCashRegisters = (eventId?: string) => {
  return useQuery({
    queryKey: eventsQueryKeys.cashRegisters(eventId!),
    queryFn: () => getCashRegisters(eventId!),
    enabled: !!eventId,
  });
};

const getPaginatedCashRegisters = async (eventId: string, params?: CashRegisterFilters) => {
  try {
    const res = await privateAxios.get<PaginatedResponse<CashRegister>>(
      `/events/${eventId}/cash-register`,
      { params },
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        message: error.response?.data.message,
      };
    }
    throw error;
  }
};

export const useGetPaginatedCashRegistersQuery = (
  eventId: string,
  params?: CashRegisterFilters,
) => {
  return useQuery({
    queryKey: eventsQueryKeys.cashRegistersWithParams(eventId, params),
    queryFn: () => getPaginatedCashRegisters(eventId, params),
    enabled: !!eventId,
  });
};

const getProEventVisitors = async (eventId: string, params?: ProEventVisitorsFilter) => {
  const res = await privateAxios.get<ProEventVisitorsResponse>(`/events/${eventId}/pro-visitors`, {
    params,
  });
  return res.data;
};

export const useGetProEventVisitorsQuery = (
  eventId: string,
  params?: ProEventVisitorsFilter,
  options?: QueryArgs<ProEventVisitorsResponse>,
) => {
  return useQuery({
    queryKey: eventsQueryKeys.proVisitorsWithParams(eventId, params),
    queryFn: () => getProEventVisitors(eventId, params),
    enabled: !!eventId,
    ...options,
  });
};
